import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useMeta from "@/hooks/useMeta";
import { works } from "@/mocks/works";
import { news } from "@/mocks/news";
import type { Work } from "@/types/media";
import { TYPE_LABELS } from "@/types/media";
import WorkCard from "@/components/base/WorkCard";
import EmptyState from "@/components/base/EmptyState";

type Tab = "works" | "news";

function matchWork(w: Work, q: string) {
  const haystack = [
    w.title,
    w.director,
    ...w.cast,
    ...w.genres,
    ...w.tags,
    w.region,
    String(w.year),
    w.synopsis,
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
}

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [tab, setTab] = useState<Tab>("works");

  useEffect(() => {
    setInput(query);
  }, [query]);

  useMeta({
    title: query ? `“${query}”的搜索结果 - 大象传媒` : "搜索影视作品与资讯 - 大象传媒",
    description: query
      ? `大象传媒为您找到与“${query}”相关的影视作品与资讯内容，支持影视与资讯双分类筛选。`
      : "在大象传媒搜索影视作品与资讯内容，支持关键词模糊匹配，覆盖电影、电视剧、综艺与行业资讯。",
    keywords: "影视搜索,影视作品搜索,资讯搜索,大象传媒",
  });

  const q = query.trim().toLowerCase();

  const workResults = useMemo(
    () => (q ? works.filter((w) => matchWork(w, q)) : []),
    [q]
  );

  const newsResults = useMemo(
    () =>
      q
        ? news.filter((n) =>
            [n.title, n.summary, ...n.tags, ...n.content]
              .join(" ")
              .toLowerCase()
              .includes(q)
          )
        : [],
    [q]
  );

  const total = workResults.length + newsResults.length;

  const hotWorks = useMemo(
    () => [...works].sort((a, b) => b.popularity - a.popularity).slice(0, 5),
    []
  );

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    if (value) setSearchParams({ q: value });
    else setSearchParams({});
  };

  const current = tab === "works" ? workResults : newsResults;

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          搜索中心
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          输入关键词，快速查找影视作品与资讯内容
        </p>
      </div>

      <form
        onSubmit={submit}
        className="mb-6 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 sm:flex-row sm:items-center"
      >
        <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5">
          <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
            <i className="ri-search-line text-base"></i>
          </span>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="例如：科幻、悬疑、三体、电影资讯"
            className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
          />
          {input && (
            <button
              type="button"
              aria-label="清空"
              onClick={() => setInput("")}
              className="flex h-4 w-4 items-center justify-center text-foreground-500 hover:text-foreground-800"
            >
              <i className="ri-close-circle-line text-base"></i>
            </button>
          )}
        </div>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
        >
          <i className="ri-search-line"></i>
          搜索
        </button>
      </form>

      {q && (
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground-600">
            关键词“<strong className="text-foreground-900">{query}</strong>”共找到{" "}
            <strong className="text-primary-700">{total}</strong> 条结果
          </span>
          <div className="ml-auto inline-flex rounded-full border border-background-300 bg-background-100 p-1">
            <button
              type="button"
              onClick={() => setTab("works")}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                tab === "works"
                  ? "bg-primary-600 text-background-50"
                  : "text-foreground-700 hover:text-primary-700"
              }`}
            >
              影视作品 ({workResults.length})
            </button>
            <button
              type="button"
              onClick={() => setTab("news")}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                tab === "news"
                  ? "bg-primary-600 text-background-50"
                  : "text-foreground-700 hover:text-primary-700"
              }`}
            >
              资讯 ({newsResults.length})
            </button>
          </div>
        </div>
      )}

      {q && current.length > 0 && tab === "works" && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {workResults.map((work) => (
            <WorkCard key={work.id} work={work} />
          ))}
        </div>
      )}

      {q && current.length > 0 && tab === "news" && (
        <div className="flex flex-col gap-4">
          {newsResults.map((item) => (
            <Link
              key={item.id}
              to={`/news/${item.id}`}
              className="group flex flex-col gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:border-primary-300 sm:flex-row"
            >
              <div className="h-36 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-auto sm:w-44">
                <img
                  src={item.cover}
                  alt={item.title}
                  title={`${item.title} 配图`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-xs text-foreground-500">
                  <span>{item.date}</span>
                  <span className="rounded-sm bg-secondary-100 px-1.5 py-0.5 text-secondary-900">
                    {TYPE_LABELS[item.category]}
                  </span>
                </div>
                <h3 className="mt-2 line-clamp-2 font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-700">
                  {item.title}
                </h3>
                <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                  {item.summary}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}

      {q && current.length === 0 && (
        <EmptyState
          icon="ri-search-eye-line"
          title={`“${query}”暂无匹配结果`}
          description="换个关键词试试，或从下方热门影视作品中寻找灵感。"
          actionLabel={tab === "works" ? "查看资讯结果" : "查看影视结果"}
          onAction={() => setTab(tab === "works" ? "news" : "works")}
        />
      )}

      {!q && (
        <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-12 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
            <i className="ri-search-2-line text-2xl"></i>
          </span>
          <h3 className="mt-3 font-heading text-lg font-semibold text-foreground-900">
            输入关键词开始搜索
          </h3>
          <p className="mt-1 text-sm text-foreground-600">
            支持影视名称、导演、演员、题材标签与资讯内容关键词
          </p>
        </div>
      )}

      {(!q || current.length === 0) && (
        <section className="mt-10">
          <div className="mb-6 flex items-center gap-3">
            <span className="h-6 w-1.5 rounded-full bg-accent-500"></span>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              热门影视推荐
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {hotWorks.map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}